import {
  FilterSelector,
  KeyValue,
} from '@delmonicos/shared-design-system-react/src/components/filter-selector/filter-selector';
import {
  useDebounce,
  usePaginatedFilter,
  usePaginatedFilterControls,
} from '@delmonicos/shared-sdk/libs/core/src/hooks';
import { GatewayNetworkModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/networks/models/outputs/gateway-network.model';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCpmsBoGetNetworksPaginatedQuery } from '@app/core/service/cpms-bo-network.service';

const LIMIT = 10;

interface NetworkFilterControllerProps {
  onChange: (items: string[]) => void;
  selectedItemIds: string[];
}

export const NetworkFilterController = ({
  onChange,
  selectedItemIds,
}: NetworkFilterControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const [search, setSearch, offset, incrementOffset, resetOffset] =
    usePaginatedFilterControls(LIMIT);
  const [isTyping, setIsTyping] = useState(false);

  const debouncedSearch = useDebounce(search);

  const { data, isFetching } = useCpmsBoGetNetworksPaginatedQuery({
    limit: LIMIT,
    offset,
    name: debouncedSearch ? `~${debouncedSearch}` : '~',
  });

  const handleMap = useCallback(
    (customer: GatewayNetworkModel): KeyValue => ({
      key: customer.id,
      value: customer.name,
    }),
    [],
  );

  const { items, updateItems } = usePaginatedFilter<KeyValue>({
    data,
    offset,
    onMap: handleMap,
  });

  useEffect(() => {
    if (debouncedSearch !== undefined) {
      setIsTyping(false);
    }
  }, [debouncedSearch]);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <FilterSelector
      items={items}
      isLoading={isFetching || isTyping}
      initialSelectedItemIds={selectedItemIds}
      onChange={onChange}
      onSearchChange={(val) => {
        setSearch(val);
        resetOffset();
        updateItems([]);
      }}
      onOffsetChange={incrementOffset}
      labels={{
        title: t('delmonicos-shared-sdk.outputs-location.network'),
        search: t('common.filter.search'),
        seeMore: t('common.filter.seeMore'),
      }}
      icon="server-06"
      hasSeeMore={!!data?.hasNextPage}
    />
  );
};
